import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MapPin, ArrowUpRight, Images, Play } from "lucide-react";
import { MediaPlaceholder } from "@/components/MediaPlaceholder";
import { cn } from "@/lib/utils";

export const ProjectCard = ({ project, className, aspect = "aspect-[4/3]" }) => {
  const cover = project.media[0];
  const photoCount = project.media.filter((m) => m.type === "image" && m.src).length;
  const videoCount = project.media.filter((m) => m.type === "video" && m.src).length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid={`project-card-${project.id}`}
          className={cn("group block w-full text-left", className)}
        >
          <div className="relative overflow-hidden border border-white/10 bg-ink-surface transition-colors duration-300 group-hover:border-gold/40">
            <MediaPlaceholder
              label={cover.label}
              type={cover.type}
              src={cover.src}
              alt={cover.alt}
              fit={cover.fit}
              aspect={aspect}
              className="transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <span className="absolute left-4 top-4 z-20 rounded-sm bg-gold px-2.5 py-1 font-heading text-[10px] font-bold uppercase tracking-wider text-ink">
              {project.serviceType}
            </span>
          </div>
          <div className="pt-5">
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-heading text-xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-gold">
                {project.title}
              </h3>
              <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-steel transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs uppercase tracking-wide text-steel">
              <MapPin className="h-3.5 w-3.5 text-gold" />
              {project.location}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-steel">{project.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-wide text-steel/80">
              <span className="flex items-center gap-1.5">
                <Images className="h-3.5 w-3.5" />
                {photoCount} Photos
              </span>
              {videoCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <Play className="h-3.5 w-3.5" />
                  {videoCount} Video
                </span>
              )}
              {project.beforeAfter && <span className="text-gold/80">Before / After</span>}
            </div>
          </div>
        </button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-white/10 bg-ink p-6 md:p-8">
        <DialogHeader className="text-left">
          <span className="inline-flex w-fit rounded-sm bg-gold px-2.5 py-1 font-heading text-[10px] font-bold uppercase tracking-wider text-ink">
            {project.serviceType}
          </span>
          <DialogTitle className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-white">
            {project.title}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-xs uppercase tracking-wide text-steel">
            <MapPin className="h-3.5 w-3.5 text-gold" />
            {project.location}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm leading-relaxed text-steel-light">{project.description}</p>
        <div className="mt-2 grid gap-4 sm:grid-cols-2">
          {project.media.map((m, i) => (
            <MediaPlaceholder
              key={i}
              label={m.label}
              type={m.type}
              src={m.src}
              poster={m.poster}
              alt={m.alt}
              fit={m.fit}
              aspect="aspect-[4/3]"
            />
          ))}
        </div>
        <p className="mt-4 border-t border-white/10 pt-4 text-xs leading-relaxed text-steel/70">
          {project.media.some((m) => m.src)
            ? "Real project photo supplied by Velasco Lock and Safe. More photos and videos from this type of work will be added over time."
            : "Placeholder layout — real photos and videos from this type of work will appear here soon."}
        </p>
      </DialogContent>
    </Dialog>
  );
};
